import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import MaterialSymbol from '../MaterialSymbol';
import useLocaleNavigation, { INTERNATIONAL_COUNTRY_CODE } from '../../services/useLocaleNavigation';
import FOCUS_RING_SX from '../focusRingSx';
import { HIGH_CONTRAST_HOVER_SWAP_SX, STRONG_BORDER_COLOR } from '../highContrastSx';

const INTERNATIONAL_REACT_KEY = 'international';

const TRIGGER_LABEL_SX = { textTransform: 'capitalize' };

const LANGUAGE_LABEL_SX = { ...TRIGGER_LABEL_SX, display: { xs: 'none', sm: 'inline' } };

export const TRIGGER_SX = theme => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    color: 'grey.500',
    cursor: 'pointer',
    flexShrink: 0,
    borderRadius: 1,
    px: 1,
    py: 0.5,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.02em',
    background: 'none',
    border: 'none',
    fontFamily: 'inherit',
    '&:hover': { color: 'primary.main', bgcolor: 'grey.50' },
    '&:focus-visible': { ...FOCUS_RING_SX, color: 'primary.main', bgcolor: 'grey.50' },
    transition: 'color 0.15s, background-color 0.15s',
    ...HIGH_CONTRAST_HOVER_SWAP_SX(theme),
});

export const MENU_PAPER_PROPS = {
    sx: {
        borderRadius: 1,
        border: '1px solid',
        borderColor: STRONG_BORDER_COLOR,
        boxShadow: '0 10px 15px rgba(0,0,0,0.1)',
        mt: 0.5,
        maxHeight: 320,
    },
};

export const MENU_ITEM_SX = theme => ({
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    color: 'grey.700',
    minWidth: 160,
    textTransform: 'capitalize',
});

export const COUNTRY_DIVIDER_SX = {
    height: 14,
    alignSelf: 'center',
    borderColor: 'grey.300',
    mx: 0.25,
};

function LocaleSelectors({
    locale,
    country = null,
    availableLocales,
    availableCountries,
    countryLabel,
    languageLabel,
    countryHidden = false,
    navigate,
}) {
    const [countryAnchor, setCountryAnchor] = useState(null);
    const [languageAnchor, setLanguageAnchor] = useState(null);
    const { changeCountry, changeLocale } = useLocaleNavigation({ locale, country, navigate });

    const currentCountryCode = country || INTERNATIONAL_COUNTRY_CODE;
    const currentCountry = availableCountries.find(option => option.code === currentCountryCode);
    const currentLocale = availableLocales.find(option => option.code === locale);

    const countryLabelText = currentCountry ? currentCountry.name : countryLabel;
    const languageLabelText = currentLocale ? currentLocale.name : languageLabel;

    const countryAriaLabel = currentCountry ? `${countryLabel}: ${currentCountry.name}` : countryLabel;
    const languageAriaLabel = currentLocale ? `${languageLabel}: ${currentLocale.name}` : languageLabel;

    const handleCountrySelect = (selectedCode) => {
        setCountryAnchor(null);
        changeCountry(selectedCode);
    };

    const handleLocaleSelect = (selectedCode) => {
        setLanguageAnchor(null);
        changeLocale(selectedCode);
    };

    return (
        <Box
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
            data-testid="locale-selectors"
        >
            {countryHidden ? null : (
                <>
                    <Box
                        component="button"
                        type="button"
                        id="redesign-header-country-trigger"
                        aria-haspopup="menu"
                        aria-expanded={Boolean(countryAnchor)}
                        aria-label={countryAriaLabel}
                        onClick={event => setCountryAnchor(event.currentTarget)}
                        sx={TRIGGER_SX}
                    >
                        <MaterialSymbol name="location_on" size={16} />
                        <Box component="span" sx={TRIGGER_LABEL_SX}>{countryLabelText}</Box>
                        <MaterialSymbol name="expand_more" size={14} />
                    </Box>
                    <Menu
                        anchorEl={countryAnchor}
                        open={Boolean(countryAnchor)}
                        onClose={() => setCountryAnchor(null)}
                        disableScrollLock
                        PaperProps={MENU_PAPER_PROPS}
                        MenuListProps={{ 'aria-labelledby': 'redesign-header-country-trigger' }}
                    >
                        {availableCountries.map(option => (
                            <MenuItem
                                key={option.code || INTERNATIONAL_REACT_KEY}
                                selected={option.code === currentCountryCode}
                                onClick={() => handleCountrySelect(option.code)}
                                sx={MENU_ITEM_SX}
                            >
                                {option.name}
                            </MenuItem>
                        ))}
                    </Menu>
                    <Divider
                        orientation="vertical"
                        flexItem
                        sx={COUNTRY_DIVIDER_SX}
                        data-testid="locale-selectors-divider"
                    />
                </>
            )}
            <Box
                component="button"
                type="button"
                id="redesign-header-language-trigger"
                aria-haspopup="menu"
                aria-expanded={Boolean(languageAnchor)}
                aria-label={languageAriaLabel}
                onClick={event => setLanguageAnchor(event.currentTarget)}
                sx={TRIGGER_SX}
            >
                <MaterialSymbol name="translate" size={16} />
                <Box component="span" sx={LANGUAGE_LABEL_SX}>{languageLabelText}</Box>
                <MaterialSymbol name="expand_more" size={14} />
            </Box>
            <Menu
                anchorEl={languageAnchor}
                open={Boolean(languageAnchor)}
                onClose={() => setLanguageAnchor(null)}
                disableScrollLock
                PaperProps={MENU_PAPER_PROPS}
                MenuListProps={{ 'aria-labelledby': 'redesign-header-language-trigger' }}
            >
                {availableLocales.map(option => (
                    <MenuItem
                        key={option.code}
                        selected={option.code === locale}
                        onClick={() => handleLocaleSelect(option.code)}
                        sx={[MENU_ITEM_SX, { minWidth: 140 }]}
                    >
                        {option.name}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}

LocaleSelectors.propTypes = {
    locale: PropTypes.string.isRequired,
    country: PropTypes.string,
    availableLocales: PropTypes.arrayOf(
        PropTypes.shape({
            code: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        }),
    ).isRequired,
    availableCountries: PropTypes.arrayOf(
        PropTypes.shape({
            code: PropTypes.string,
            name: PropTypes.string.isRequired,
        }),
    ).isRequired,
    countryLabel: PropTypes.string.isRequired,
    languageLabel: PropTypes.string.isRequired,
    countryHidden: PropTypes.bool,
    navigate: PropTypes.func,
};

export default LocaleSelectors;
